import { getProtocolConfig, GLOBAL_CONFIG } from '@silo/config';
import {
  Address,
  AMOUNT_ZERO,
  CHECKSUM_ADDRESS_ZERO,
  DEFAULT_PROTOCOL_KEY,
  getChecksumAddressOrThrow,
  LINKS,
  ProtocolKey,
  USD_DECIMALS,
  USD_ZERO,
} from '@silo/types';
import { Button, DashboardRewards, ExternalLink, Modal, useModalState } from '@silo/ui';
import { useSiloWeb3 } from '@silo/web3';
import { useQueryRewardsData } from 'app/queries/use-query-rewards-data';
import { Array, Option, pipe } from 'effect';
import { isNotNullable, isNullable } from 'effect/Predicate';
import { ClaimRewardsForm } from '../silo/rewards/ClaimRewardsForm';
import { useQueryRevenueData } from 'app/queries/use-query-revenue-data';
import { ClaimRevenueForm } from '../revenue/claim-revenue-form';

const IS_CLAIM_MANUALLY_DISABLED: Record<ProtocolKey, boolean> = {
  mainnetLegacy: false,
  mainnetLlama: true,
  mainnetGrand: false,
  arbitrum: false,
  optimism: false,
  base: false,
  lrtbase: false,
  local: false,
  anvil: false,
};

export function Rewards(): JSX.Element {
  const { data: rewardsData } = useQueryRewardsData();
  const { data: revenueData } = useQueryRevenueData();
  const { protocolInstance, account } = useSiloWeb3();

  const {
    isModalOpen: isRewardsModalOpen,
    openModal: openRewardsModal,
    closeModal: closeRewardsModal,
  } = useModalState();

  const {
    isModalOpen: isRevenueModalOpen,
    openModal: openRevenueModal,
    closeModal: closeRevenueModal,
  } = useModalState();

  const protocolKey = protocolInstance ?? DEFAULT_PROTOCOL_KEY;
  const nativeRewardsOption = pipe(
    getProtocolConfig(protocolKey),
    (config) => config.nativeRewards,
    Option.fromNullable
  );

  const rewardsTokenSymbol = pipe(
    nativeRewardsOption,
    Option.map((nativeRewards) => nativeRewards.symbol),
    Option.getOrElse(() => '')
  );

  const siloIncentivesControllerAddress = pipe(
    nativeRewardsOption,
    Option.map((nativeRewards) => nativeRewards.siloIncentivesControllerAddress),
    Option.map(getChecksumAddressOrThrow),
    Option.getOrElse(() => CHECKSUM_ADDRESS_ZERO)
  );

  const shareTokenAddresses = pipe(
    nativeRewardsOption,
    Option.map(({ historicalShareTokens, tokenAddress }) => {
      return historicalShareTokens[tokenAddress] ?? [];
    }),
    Option.map((addresses) => [...new Set([...addresses])]),
    Option.getOrElse((): Address[] => []),
    Array.map(getChecksumAddressOrThrow)
  );

  if (isNullable(rewardsData)) {
    return <DashboardRewards.PulseDashboardRewards />;
  }

  const {
    data: { nativeRewards, merklRewards },
  } = rewardsData;

  const nativeRewardsBalance = nativeRewards?.balance ?? AMOUNT_ZERO;
  const nativeRewardsBalanceUsd = nativeRewards?.balanceUsd ?? USD_ZERO;
  const merklRewardsBalanceUsd = merklRewards?.totalUsd ?? USD_ZERO;

  const isClaimManuallyDisabled = IS_CLAIM_MANUALLY_DISABLED[protocolKey];
  const isModalDisabled = nativeRewardsBalance === AMOUNT_ZERO || isClaimManuallyDisabled;

  const merklLink = LINKS.MERKL_REWARDS + (account ?? '');

  const isRevenueModalDisabled = isNotNullable(revenueData) && revenueData.revenueBalance === 0n;

  return (
    <div>
      <DashboardRewards
        protocolInstance={protocolKey}
        rewardsTokenSymbol={rewardsTokenSymbol}
        baseRewardsUsd={nativeRewardsBalanceUsd}
        merklRewardsUsd={merklRewardsBalanceUsd}
        baseClaim={
          <Button
            variant="action"
            onClick={openRewardsModal}
            disabled={isModalDisabled}
            className={DashboardRewards.buttonClassName}
          >
            Claim
          </Button>
        }
        merklClaim={
          <ExternalLink href={merklLink} size="normal" variant="action" className={DashboardRewards.buttonClassName}>
            Claim on Merkl
          </ExternalLink>
        }
        detailsLink={LINKS.SILOPEDIA.INCENTIVES}
        revenueTokenSymbol={GLOBAL_CONFIG.revenue.rewardTokenSymbol}
        revenueBalanceUsd={revenueData?.revenueBalanceUsd ?? 0n}
        isRevenueProtocol={protocolInstance === GLOBAL_CONFIG.revenue.protocolKey}
        revenueClaim={
          <Button
            variant="action"
            onClick={openRevenueModal}
            disabled={isRevenueModalDisabled}
            className={DashboardRewards.buttonClassName}
          >
            Claim
          </Button>
        }
      />

      <Modal isOpen={isRewardsModalOpen} closeModal={closeRewardsModal}>
        <ClaimRewardsForm
          siloIncentivesControllerAddress={siloIncentivesControllerAddress}
          totalBalance={nativeRewardsBalance}
          totalBalanceUSD={{
            value: nativeRewardsBalanceUsd,
            decimals: USD_DECIMALS,
          }}
          shareTokenAddresses={shareTokenAddresses}
          protocolInstance={protocolKey}
        />
      </Modal>

      {isNotNullable(revenueData) && (
        <Modal isOpen={isRevenueModalOpen} closeModal={closeRevenueModal}>
          <ClaimRevenueForm
            revenueBalance={revenueData.revenueBalance}
            revenueBalanceUsd={revenueData.revenueBalanceUsd}
          />
        </Modal>
      )}
    </div>
  );
}
